/**
 * external dependencies
 */
import { TabulatorFull } from 'tabulator-tables';

/**
 * internal dependencies
 */
import { showToast } from "../../toasts";
import { dropdown, toggleDropDown } from "../../dropdown";
import { FILTER_SECTION, PAYMENT_TYPE_LIST } from "../common/filter-elements";
import { handleFilterSectionToggle } from "../common/filter-section-toggle";
import { parsePartnerDataForSort } from "../common/partner-dropdown";
import { filterClear } from "../common/filter-clear";
import { terminalDropDown } from "../common/terminal-dropdown";
import { setDateFilter } from "../common/set-input-filter";
import { ensureDateRangeWithin31Days } from "../tableHelpers/transaction-helpers";
import { getInitalTransactionData } from "../data/get-transactions";
import getPartners from "../../../routes/get-partners";

/**
 * Handles the transaction table filter functionalities, including setting up event listeners for filter inputs and managing the filter state.
 * 
 * @param tableObject the table class instance
 * @param startDate the start date for the filter
 * @param endDate the end date for the filter
 * @returns void
 */
export const transactionTableFilter = async (tableObject: TabulatorFull, startDate: Date, endDate: Date): Promise<void> => {
  if (typeof tableObject != "object" || tableObject === null || tableObject === undefined) return;

  FILTER_SECTION.startDate!.valueAsDate = startDate;
  FILTER_SECTION.endDate!.valueAsDate = endDate;
  FILTER_SECTION.endDate!.max = new Date().toISOString().split("T")[0];

  dropdown(PAYMENT_TYPE_LIST, "one", "Payments", false, true, true);

  if ((FILTER_SECTION as any).partnerList) {
    const partner = await getPartners();
    parsePartnerDataForSort(partner.body);
    toggleDropDown("#aafp_dropdown-partner", "aafpDropdown", ".aafp_select-data-container-partner", ".arrow-partner");
  }

  handleFilterSectionToggle(".aafp_filter-header");
  // handles setting the start date if the end date is changed to ensure the date range is within 31 days. 
  FILTER_SECTION.startDate!.addEventListener("change", () => setUpdateStartEndDate(endDate));
  // handles searching the table
  FILTER_SECTION.searchFilterButton!.addEventListener("click", () => searchTransactionTable(tableObject));
  // handles clearing all filter inputs and clearing the table of the search results
  FILTER_SECTION.clearFilterButton!.addEventListener("click", () => clearTransactionFilter(tableObject));
};

/**
 * Sets the start date if the end date is changed to ensure the date range is within 31 days.
 * 
 * @param endDate the end date to compare against the start date
 * 
 * @returns void
 */
const setUpdateStartEndDate = (endDate: Date): void => {
  const [adjustedStartDate, adjustedEndDate] = ensureDateRangeWithin31Days(new Date(FILTER_SECTION.startDate!.value), endDate);
  FILTER_SECTION.endDate!.valueAsDate = adjustedEndDate;
  FILTER_SECTION.endDate!.max = new Date(adjustedEndDate).toISOString().split("T")[0];
};

/**
 * Get data from input fields and sets a filter on the table object. This function cannot be put into
 * the table-helpers file due to different amount of fields that can be used as a filter.
 * 
 * @param {TabulatorFull} table the table class instance. 
 * 
 * @returns void
 */
const searchTransactionTable = async (table: TabulatorFull): Promise<void> => {
  const startDate = document.querySelector("#search-date-start") as HTMLInputElement;
  const endDate = document.querySelector("#search-date-end") as HTMLInputElement;
  const selectedPartners = Array.from(document.querySelectorAll(".aafp_part")) as HTMLInputElement[];
  const name = document.querySelector("#aafp_cardname-list-sort") as HTMLInputElement;
  const customer = document.querySelector("#aafp_customer-list-sort") as HTMLInputElement;
  const payment = document.querySelector("#aafp_dropdown-button-one") as HTMLElement;
  const group = document.querySelector("#aafp_cardgroup-list-sort") as HTMLInputElement;
  const card = document.querySelector("#aafp_cardnumber-list-sort") as HTMLInputElement;
  const terminal = document.querySelector("#aafp_terminal-list-sort") as HTMLInputElement;
  //const downLoadError = document.querySelector("#include-errors") as HTMLInputElement;

  FILTER_SECTION.searchFilterButton!.classList.add("aafp_button-loading");
  const partner = selectedPartners.filter((ele) => ele.checked).map((ele) => +ele.value);
  const filterArr: any[] = partner.length > 0 ? [
    {
      field: "partnerId",
      type: "in",
      value: partner
    }
  ] : [];

  if (typeof payment.dataset.value !== "undefined" && payment.dataset.value !== "2") filterArr.push({
      field: "paymentMethod",
      type: "=",
      value: payment.dataset.value
  });
  if (card.value !== "") filterArr.push({
      field: "cardNumber",
      type: "like",
      value: card.value
  });
  if (group.value !== "") filterArr.push({
      field: "cardGroupName",
      type: "like",
      value: group.value
  });
  if (name.value != "") filterArr.push({
      field: "cardName",
      type: "like",
      value: name.value
  });
  if (customer.value != "") filterArr.push({
      field: "customerName",
      type: "like",
      value: customer.value
  });
  if (typeof terminal.value !== "undefined" && terminal.value !== "") filterArr.push({
      field: "terminalId",
      type: "like",
      value: terminal.value
  });

  const [year, month, day] = startDate.value.split('-');
  //@ts-ignore
  const dateOne = new Date(year, month - 1, day);
  const [yearTwo, monthTwo, dayTwo] = endDate.value.split('-');
  //@ts-ignore
  const dateTwo = new Date(yearTwo, monthTwo - 1, dayTwo);

  try {
    const transactionData = await getInitalTransactionData(dateOne, dateTwo);
    if (!transactionData) {
      throw new Error("Error fetching transaction data");
    }

    table.replaceData(transactionData)
    .then(function () {
      let data = table.getData();
      //terminalDropDown(data);
      
    }).catch(function (error: any) {
      //handle error loading data
      throw new Error("Error fetching transaction data");
    });

    table.setFilter(filterArr);
  } catch (error) {
    showToast("aafp_toast-alert", "Error fetching transaction data. Please try again later.");
    console.error("searchTransactionTable", error);
  } finally {
    FILTER_SECTION.searchFilterButton!.classList.remove("aafp_button-loading");
  }
};

/**
 * Clears all filter input fields and resets the table data.
 * 
 * @param {TabulatorFull} table the table class instance.
 * 
 * @returns void
 */
const clearTransactionFilter = async (table: TabulatorFull): Promise<void> => {
  FILTER_SECTION.clearFilterButton!.classList.add("aafp_button-loading");
  filterClear(".table-filter-clear");

  let startDate = setDateFilter();
  FILTER_SECTION.startDate!.valueAsDate = startDate;
  let endDate = new Date();
  FILTER_SECTION.endDate!.valueAsDate = endDate;
  FILTER_SECTION.endDate!.max = new Date().toISOString().split("T")[0];

  const transactionData = await getInitalTransactionData(startDate, endDate);
  if (!transactionData) {
      showToast("aafp_toast-alert", "Error fetching transaction data. Please try again later.");
      return;
  }

  table.replaceData(transactionData);
  table.clearFilter(true);
  
  FILTER_SECTION.clearFilterButton!.classList.remove("aafp_button-loading");
};
